import LegalPage, {
  type LegalSection,
} from "@/components/landing/dcv2/LegalPage";

/**
 * Terms of Service.
 *
 * THE EFFECTIVE DATE IS A LITERAL, NOT `new Date()`.
 *
 * Computing it would make the page claim it took effect today, every day, for
 * the rest of its life — so a visitor could never tell which version they
 * agreed to, and neither could we. A legal effective date changes when the
 * document changes. Edit this string when the terms are revised.
 */
const EFFECTIVE_DATE = "28 August 2026";

const SECTIONS: LegalSection[] = [
  {
    n: "1",
    title: "Acceptance of Terms",
    blocks: [
      {
        p: "By accessing or using AI2Aim’s software-as-a-service (SaaS) platform, artificial intelligence tools, APIs, website, and related services (collectively, the “Service”), you confirm that you can form a binding contract with AI2Aim and that you accept these Terms. If you are using the Service on behalf of a company or other legal entity, you represent that you have the authority to bind that entity to these Terms.",
      },
    ],
  },
  {
    n: "2",
    title: "The AI2Aim Service & AI Outputs",
    blocks: [
      {
        bullets: [
          {
            lead: "Artificial Intelligence Capabilities",
            text: "Our Service incorporates artificial intelligence and machine learning models to generate insights, text, code, or other outputs (“AI Outputs”) based on your inputs (“User Inputs”).",
          },
          {
            lead: "Accuracy & Use of Outputs",
            text: "You acknowledge that AI technology is probabilistic and may occasionally generate incorrect, incomplete, or objectionable content. You are solely responsible for evaluating the accuracy and appropriateness of any AI Outputs for your specific use case.",
          },
          {
            lead: "Beta Features",
            text: "From time to time, we may offer experimental or “beta” features. These are provided “as-is” without any warranties and may be modified or discontinued at any time.",
          },
        ],
      },
    ],
  },
  {
    n: "3",
    title: "User Accounts & Registration",
    blocks: [
      {
        bullets: [
          {
            lead: "Account Security",
            text: "You are responsible for safeguarding your login credentials and for any activities or actions occurring under your account.",
          },
          {
            lead: "Eligibility",
            text: "You must be at least 18 years of age (or the age of majority in your jurisdiction) to register for and use the Service.",
          },
        ],
      },
    ],
  },
  {
    n: "4",
    title: "Intellectual Property & Data Ownership",
    blocks: [
      {
        bullets: [
          {
            lead: "Your Data",
            text: "You retain all right, title, and interest in and to your User Inputs. You grant AI2Aim a worldwide, non-exclusive license to host, process, and use your User Inputs solely to provide, maintain, and improve the Service.",
          },
          {
            lead: "AI Outputs",
            text: "Subject to your compliance with these Terms, AI2Aim assigns to you all its right, title, and interest in and to the specific AI Outputs generated for you by the Service.",
          },
          {
            lead: "AI2Aim IP",
            text: "The underlying software, algorithms, system architecture, trademarks, and documentation remain the exclusive property of AI2Aim.",
          },
        ],
      },
    ],
  },
  {
    n: "5",
    title: "Acceptable Use Policy",
    blocks: [
      { p: "You agree not to misuse the Service. Specifically, you agree not to:" },
      {
        bullets: [
          "Use the Service to generate spam, malware, hate speech, harassment, or content that violates applicable laws.",
          "Reverse engineer, decompile, or attempt to extract the source code of our AI models or software platform.",
          "Input sensitive personal data, classified information, or medical records into the AI platform unless covered by a separate enterprise agreement.",
          "Overload, disrupt, or attempt to gain unauthorized access to our infrastructure or API endpoints.",
        ],
      },
    ],
  },
  {
    n: "6",
    title: "AI Usage Policy",
    blocks: [
      {
        bullets: [
          {
            lead: "Scope & Permitted Use",
            text: "AI tools must be used strictly for lawful business and operational purposes. Users retain prompt ownership and are fully responsible for all inputs submitted and decisions made based on generations.",
          },
          {
            lead: "Prohibited Conduct",
            text: "Users are strictly prohibited from generating harmful content (e.g., hate speech, malware instructions), engaging in misrepresentation or deepfakes, infringing third-party intellectual property, automating high-risk decisions (such as legal, financial, or medical determinations) without human oversight, or attempting to bypass security guardrails and reverse-engineer models.",
          },
          {
            lead: "Data Privacy",
            text: "Proprietary data is not used to train public or foundational third-party AI models without explicit enterprise agreement. Highly sensitive information like unencrypted PCI data, HIPAA-protected PHI, or government IDs must not be entered into standard tiers.",
          },
          {
            lead: "Violations",
            text: "Non-compliance constitutes a material breach, leading to immediate access suspension or termination.",
          },
        ],
      },
    ],
  },
  {
    n: "7",
    title: "AI Disclaimer",
    blocks: [
      {
        bullets: [
          {
            lead: "Nature of AI",
            text: "AI-generated outputs are probabilistic, experimental, and inherently prone to errors, hallucinations, or bias.",
          },
          {
            lead: "No Professional Advice",
            text: "Platform outputs serve solely for informational and operational assistance and do not constitute legal, financial, medical, tax, or professional advice.",
          },
          {
            lead: "User Responsibility",
            text: "Users maintain mandatory “human-in-the-loop” oversight to validate all outputs, ensure legal compliance, and protect sensitive data before relying on them for critical operations.",
          },
          {
            lead: "Warranty & “As-Is” Basis",
            text: "The Service and AI outputs are provided on an “AS IS” and “AS AVAILABLE” basis without warranties of any kind, express or implied, including merchantability or fitness for a particular purpose.",
          },
        ],
      },
    ],
  },
  {
    n: "8",
    title: "Subscription Fees & Payment",
    blocks: [
      {
        p: "Certain features of AI2Aim require paid subscriptions. Fees, billing cycles, and renewal terms will be clearly presented at the time of purchase. All fees are non-refundable except as required by law.",
      },
    ],
  },
  {
    n: "9",
    title: "Limitation of Liability",
    blocks: [
      {
        p: "To the maximum extent permitted by applicable law, AI2Aim, its founders, employees, and partners shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, data, or goodwill resulting from your use of the Service or AI Outputs.",
      },
    ],
  },
];

export default function Terms() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Terms of Service"
      effectiveDate={EFFECTIVE_DATE}
      intro="Welcome to AI2Aim! By checking the box during sign-up, creating an account, or otherwise using our platform, you agree to be bound by these Terms of Service."
      sections={SECTIONS}
      footNote="If you have any questions or concerns regarding these Terms, please reach out to our team at contact@ai2aim.ai."
    />
  );
}
